import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Result, Spin, Modal } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../config';
import { useNavigate } from 'react-router-dom';
import useActivePeriod from '../hooks/useActivePeriod';

const Register = () => {
    const [form] = Form.useForm();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();
    const { loading: periodLoading, hasActivePeriod } = useActivePeriod();

    const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

    const onFinish = async (values) => {
        
        try {
            setLoading(true);
            setErrors({}); // Clear previous errors
            const response = await axios.post(`${config.apiUrl}/api/v1/register`, {
                name: values.name,
                email: values.email,
                phone: values.phone,
                password: values.password,
                password_confirmation: values.password_confirmation,
            });
            
            setEmail(values.email);
            setSuccessMessage(response.data.message || 'Đăng ký thành công! Vui lòng kiểm tra email để xác thực tài khoản.');
            setIsSubmitted(true);

            message.success(response.data.message || 'Đăng ký thành công!');
            form.resetFields();

            // Redirect to login page after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);

        } catch (error) {
            if (error.response?.data?.errors) {
                // Handle validation errors from backend
                const backendErrors = error.response.data.errors;
                setErrors(backendErrors);
                
                // Set form errors
                Object.keys(backendErrors).forEach(field => {
                    form.setFields([
                        {
                            name: field,
                            errors: [backendErrors[field][0]],
                            touched: true
                        }
                    ]);
                });
            } else if (error.response?.data?.error_code === 'NO_ACTIVE_PERIOD') {
                message.error(error.response.data.message || 'Hiện không có đợt tuyển sinh nào đang mở để đăng ký.');
            } else if (error.response?.data?.message) {
                // Handle general error message
                message.error(error.response.data.message);
            } else {
                // Handle network or other errors
                message.error('Có lỗi xảy ra. Vui lòng thử lại sau!');
            }
        } finally {
            setLoading(false);
        }
    };


    if (!periodLoading && !hasActivePeriod) {
        return (
            <Card style={{ maxWidth: 600, margin: '0 auto', marginTop: 20 }}>
                <Result
                    status="info"
                    title="Hiện không có đợt tuyển sinh nào đang mở"
                    subTitle="Vui lòng quay lại sau."
                    extra={[
                        <Button type="primary" key="home" onClick={() => navigate('/')}>
                            Về trang chủ
                        </Button>,
                    ]}
                />
            </Card>
        );
    }

    if (isSubmitted) {
        return (
            <Card style={{ maxWidth: 600, margin: '0 auto', marginTop: 20 }}>
                <Result
                    status="success"
                    title="Đăng ký thành công!"
                    subTitle={
                        <div>
                            <p>{successMessage}</p>
                            <p>Email: <strong>{email}</strong></p>
                            <p>Nếu bạn không nhận được email, vui lòng kiểm tra thư mục spam hoặc thử lại.</p>
                            <p>Bạn sẽ được chuyển hướng đến trang đăng nhập sau 3 giây...</p>
                        </div>
                    }
                    extra={[
                        <Button type="primary" key="login" onClick={() => navigate('/login')}>
                            Đến trang đăng nhập
                        </Button>,
                    ]}
                />
            </Card>
        );
    }

    return (
        <>
            <Modal
                title="Đang xử lý"
                open={loading}
                footer={null}
                closable={false}
                centered
                width={300}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Spin indicator={antIcon} />
                    <p style={{ marginTop: 10 }}>Vui lòng đợi trong giây lát...</p>
                </div>
            </Modal>

            <Card 
                title="Đăng ký tài khoản" 
                style={{ 
                    maxWidth: 600, 
                    margin: '0 auto', 
                    marginTop: 20,
                    position: 'relative',
                    opacity: loading ? 0.7 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    disabled={loading}
                >
                    <Form.Item
                        label="Họ và tên"
                        name="name"
                        validateStatus={errors.name ? 'error' : ''}
                        help={errors.name?.[0]}
                        rules={[
                            { required: true, message: 'Vui lòng nhập họ và tên!' },
                            { min: 2, message: 'Họ và tên phải có ít nhất 2 ký tự!' },
                            { max: 100, message: 'Họ và tên không được vượt quá 100 ký tự!' }
                        ]}
                    >
                        <Input placeholder="Nhập họ và tên" />
                    </Form.Item>

                    <Form.Item
                        label="Email"
                        name="email"
                        validateStatus={errors.email ? 'error' : ''}
                        help={errors.email?.[0]}
                        rules={[
                            { required: true, message: 'Vui lòng nhập email!' },
                            { type: 'email', message: 'Email không hợp lệ!' }
                        ]}
                    >
                        <Input
                            placeholder="Nhập email"
                            autoComplete="username"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Xác nhận email"
                        name="email_confirmation"
                        dependencies={['email']}
                        rules={[
                            { required: true, message: 'Vui lòng xác nhận email!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('email') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Email xác nhận không khớp!'));
                                },
                            }),
                        ]}
                    >
                        <Input
                            placeholder="Nhập lại email"
                            autoComplete="off"
                            onPaste={(e) => e.preventDefault()}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Số điện thoại"
                        name="phone"
                        validateStatus={errors.phone ? 'error' : ''}
                        help={errors.phone?.[0]}
                        rules={[
                            { required: true, message: 'Vui lòng nhập số điện thoại!' },
                            { pattern: /^[0-9]{10}$/, message: 'Số điện thoại phải có 10 chữ số!' }
                        ]}
                    >
                        <Input 
                            placeholder="Nhập số điện thoại" 
                            autoComplete="tel"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Mật khẩu"
                        name="password"
                        validateStatus={errors.password ? 'error' : ''}
                        rules={[
                            { required: true, message: 'Vui lòng nhập mật khẩu!' },
                            {
                                validator: (_, value) => {
                                    if (!value) {
                                        return Promise.resolve();
                                    }

                                    const errors = [];
                                    
                                    if (value.length < 8) {
                                        errors.push('ít nhất 8 ký tự');
                                    }
                                    if (value.length > 50) {
                                        errors.push('không quá 50 ký tự');
                                    }
                                    if (!/[A-Z]/.test(value)) {
                                        errors.push('chữ in hoa');
                                    }
                                    if (!/[a-z]/.test(value)) {
                                        errors.push('chữ thường');
                                    }
                                    if (!/\d/.test(value)) {
                                        errors.push('số');
                                    }
                                    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                                        errors.push('ký tự đặc biệt');
                                    }

                                    if (errors.length > 0) {
                                        return Promise.reject(`Mật khẩu phải có ${errors.join(', ')}!`);
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input.Password 
                            placeholder="Nhập mật khẩu" 
                            autoComplete="new-password"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Xác nhận mật khẩu"
                        name="password_confirmation"
                        validateStatus={errors.password_confirmation ? 'error' : ''}
                        help={errors.password_confirmation?.[0]}
                        dependencies={['password']}
                        rules={[
                            { required: true, message: 'Vui lòng xác nhận mật khẩu!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password 
                            placeholder="Xác nhận mật khẩu" 
                            autoComplete="new-password"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            block 
                            loading={loading}
                            disabled={loading}
                        >
                            {loading ? 'Đang đăng ký...' : 'Đăng ký'}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </>
    );
};

export default Register; 